// ---------------------------------------------------------------------------
// Default command registry for the Cmd/Ctrl+K palette.
//
// Commands are split into groups (Navigation, View, Workspace, Help). Each
// command receives a `ctx` object with:
//   - `navigate(path)`   \u2014 react-router navigation
//   - `isSignedIn`       \u2014 Boolean snapshot of auth state
//
// Use `available(ctx)` to hide a command in contexts where it wouldn't work
// (e.g. "Sign out" when not signed in). The palette re-evaluates availability
// every time it opens.
// ---------------------------------------------------------------------------

function dispatchNetbase(event, detail) {
  window.dispatchEvent(new CustomEvent(event, detail ? { detail } : undefined));
}

function isOnEditorPage(ctx) {
  const path = String(ctx?.pathname || "");
  return path.startsWith("/components/router") || path.startsWith("/components/switch");
}

export const DEFAULT_COMMANDS = [
  // Navigation
  {
    id: "nav.dashboard",
    group: "Navigation",
    label: "Go to Dashboard",
    hint: "g h",
    run: (ctx) => ctx.navigate("/dashboard")
  },
  {
    id: "nav.router",
    group: "Navigation",
    label: "Go to Router editor",
    hint: "g r",
    run: (ctx) => ctx.navigate("/components/router")
  },
  {
    id: "nav.switch",
    group: "Navigation",
    label: "Go to Switch editor",
    hint: "g s",
    run: (ctx) => ctx.navigate("/components/switch")
  },
  {
    id: "nav.vlan",
    group: "Navigation",
    label: "Go to VLAN editor",
    hint: "g v",
    run: (ctx) => ctx.navigate("/components/vlan")
  },
  {
    id: "nav.configurations",
    group: "Navigation",
    label: "Go to Configurations overview",
    hint: "g c",
    run: (ctx) => ctx.navigate("/components/configurations")
  },
  {
    id: "nav.fabric",
    group: "Navigation",
    label: "Go to Fabric (live topology)",
    hint: "g f",
    run: (ctx) => ctx.navigate("/components/fabric")
  },
  {
    id: "nav.inventory",
    group: "Navigation",
    label: "Go to Inventory",
    hint: "g i",
    run: (ctx) => ctx.navigate("/components/inventory")
  },
  {
    id: "nav.deployment",
    group: "Navigation",
    label: "Go to Deployments",
    hint: "g d",
    run: (ctx) => ctx.navigate("/components/deployment")
  },
  {
    id: "nav.connectors",
    group: "Navigation",
    label: "Go to Connectors",
    hint: "g k",
    run: (ctx) => ctx.navigate("/components/connectors")
  },
  {
    id: "nav.projects",
    group: "Workspace",
    label: "Open Projects page",
    hint: "g p",
    run: (ctx) => ctx.navigate("/projects"),
    available: (ctx) => ctx.isSignedIn
  },

  // Workspace
  {
    id: "workspace.newProject",
    group: "Workspace",
    label: "New project\u2026",
    run: (ctx) => ctx.navigate("/projects?new=1"),
    available: (ctx) => ctx.isSignedIn
  },
  {
    id: "workspace.switchProject",
    group: "Workspace",
    label: "Switch project\u2026",
    run: (ctx) => ctx.navigate("/projects"),
    available: (ctx) => ctx.isSignedIn
  },
  {
    id: "workspace.revealActiveProject",
    group: "Workspace",
    label: "Reveal active project in Projects",
    run: (ctx) => ctx.navigate("/projects"),
    available: (ctx) => ctx.isSignedIn
  },
  {
    id: "workspace.signIn",
    group: "Workspace",
    label: "Sign in\u2026",
    run: () => dispatchNetbase("netbase:showEmailLogin"),
    available: (ctx) => !ctx.isSignedIn
  },

  // Editor-scoped actions. Only surface on the Router/Switch editor pages so
  // the palette doesn't offer "Import" while the user is on the Dashboard.
  {
    id: "editor.importRunningConfig",
    group: "Editor",
    label: "Import from running-config\u2026",
    run: () => dispatchNetbase("netbase:openImportDialog"),
    available: isOnEditorPage
  },
  {
    id: "editor.openFullConfig",
    group: "Editor",
    label: "View full generated config",
    run: () => dispatchNetbase("netbase:openFullConfig"),
    available: isOnEditorPage
  },
  {
    id: "editor.copyHostname",
    group: "Editor",
    label: "Copy current device hostname",
    run: () => dispatchNetbase("netbase:copyActiveHostname"),
    available: isOnEditorPage
  },
  {
    id: "editor.viewDeviceHistory",
    group: "Editor",
    label: "View device config history\u2026",
    run: (ctx) => ctx.navigate("/components/inventory"),
    available: (ctx) => ctx.isSignedIn
  },

  // View
  {
    id: "view.toggleTheme",
    group: "View",
    label: "Toggle theme (light / dark / auto)",
    run: () => dispatchNetbase("netbase:toggleTheme")
  },
  {
    id: "view.flushPendingEdits",
    group: "View",
    label: "Flush pending edits to the cloud",
    hint: "Ctrl+S",
    run: () => dispatchNetbase("netbase:forceSync")
  },

  // Help
  {
    id: "help.shortcuts",
    group: "Help",
    label: "Show keyboard shortcuts",
    hint: "?",
    run: () => dispatchNetbase("netbase:openCheatSheet")
  }
];

/**
 * Filter commands by availability and a fuzzy query.
 * @param {Array} commands
 * @param {{ navigate: (p: string) => void, isSignedIn: boolean }} ctx
 * @param {string} query
 */
export function selectCommands(commands, ctx, query) {
  const available = commands.filter((command) =>
    typeof command.available === "function" ? command.available(ctx) : true
  );

  const q = String(query || "").trim().toLowerCase();
  if (!q) return available.map((command, index) => ({ ...command, score: 0, index }));

  return available
    .map((command) => ({ ...command, score: fuzzyMatch(q, command.label) }))
    .filter((command) => command.score >= 0)
    .sort((left, right) => right.score - left.score);
}

function fuzzyMatch(query, label) {
  const q = query.toLowerCase();
  const l = label.toLowerCase();
  if (!q) return 0;
  if (l.startsWith(q)) return 2;
  if (l.includes(q)) return 1;
  return -1;
}
